// ============================================================================
// OBP : carrousel d'upsell du tiroir panier « Un autre cadeau à faire ? ».
// Remplace l'upsell de l'app EA Sticky Cart, avec LA différence clé :
// les prix affichés sont les prix REMISÉS par le moteur d'offres maison
// (simulation locale via obp-cart-rules.js ; la vraie remise est appliquée
// par la Function OBP Discounts au checkout).
//
// Rendu 100 % client : le HTML serveur ne contient que la coquille
// <obp-cart-upsell>. Après chaque morph/re-render du tiroir (Horizon),
// la coquille revient vide → on re-render (MutationObserver + cartUpdate).
// ============================================================================
import { ThemeEvents, CartUpdateEvent, CartAddEvent } from '@theme/events';
// via l'importmap (scripts.liquid) : un import relatif donnerait une URL sans
// ?v=, que le CDN Shopify passe dans un minifieur legacy es5 qui échoue.
import {
  priceIfAdded,
  companionsForCart,
  suggestionsForCart,
  redundantForCart,
  OBP_COMPLEMENTARY_PRODUCTS,
  OBP_ENSEMBLE_PARTS,
  OBP_UPSELL_EXCLUDED,
} from '@theme/obp-cart-rules';

const MAX_UPSELLS = 5;

class ObpCartUpsell extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  #renderToken = 0;
  #scheduled = null;
  #selfMutation = false;
  /** @type {Map<string, any>} cache produit par handle (session tiroir) */
  static #productCache = new Map();
  /** @type {Map<string, Promise<any[]>>} complémentaires S&D par produit (session tiroir) */
  static #complementaryCache = new Map();

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    document.addEventListener(ThemeEvents.cartUpdate, this.#scheduleRender, { signal });

    // Le morph de section Horizon remet la coquille serveur (vide) : on re-render.
    const observer = new MutationObserver(() => {
      if (this.#selfMutation) return;
      this.#scheduleRender();
    });
    observer.observe(this, { childList: true });
    signal.addEventListener('abort', () => observer.disconnect());

    this.#render();

    // Filet de sécurité : au chargement d'une page avec un panier DÉJÀ rempli
    // (visiteur qui revient), le morph d'hydratation Horizon peut vider notre
    // coquille juste après le 1er render, sans qu'un cartUpdate ne suive.
    // On re-render une fois la section stabilisée (#render est idempotent).
    setTimeout(() => {
      if (!this.querySelector('.obp-upsell__card')) this.#render();
    }, 600);
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  #scheduleRender = () => {
    clearTimeout(this.#scheduled);
    this.#scheduled = setTimeout(() => this.#render(), 250);
  };

  async #render() {
    const token = ++this.#renderToken;
    try {
      const cart = await fetch('/cart.js').then((r) => r.json());
      if (token !== this.#renderToken) return;
      if (!cart.items?.length) {
        this.#write('');
        return;
      }

      const candidates = await this.#collectCandidates(cart);
      if (token !== this.#renderToken) return;
      if (!candidates.length) {
        this.#write('');
        return;
      }

      this.#writeCarousel(cart, candidates);
    } catch (error) {
      console.error('[OBP upsell]', error);
    }
  }

  /**
   * Ordre : compléter la collection (Coffret 1 → 2), compagnons remisés des
   * règles, produits complémentaires choisis par Mayeul dans Search & Discovery,
   * puis familles maison. Hors produits déjà au panier et doublons, max 5.
   */
  async #collectCandidates(cart) {
    const inCartProducts = new Set(cart.items.map((item) => String(item.product_id)));
    const inCartVariants = new Set(cart.items.map((item) => String(item.variant_id)));
    // Ensemble ↔ pièces séparées : jamais l'un si l'autre est au panier.
    const redundant = redundantForCart(cart.items);
    /** @type {Array<{product: any, variant: any}>} */
    const pool = [];
    const pooledVariants = new Set();
    const pooledProducts = new Set();

    // Exclusions : tote bag (offert avec les plateaux), enveloppe, Imparfaits,
    // doublons avec le panier ou avec une suggestion déjà retenue.
    const isExcluded = (product) => {
      const pid = String(product.id);
      if (OBP_UPSELL_EXCLUDED.has(pid) || redundant.has(pid)) return true;
      if (/^imparfait/i.test(String(product.title))) return true;
      for (const [ensemble, parts] of OBP_ENSEMBLE_PARTS) {
        const partIds = parts.map(String);
        if (pid === String(ensemble) && partIds.some((p) => pooledProducts.has(p))) return true;
        if (partIds.includes(pid) && pooledProducts.has(String(ensemble))) return true;
      }
      return false;
    };

    // Ajoute une VARIANTE précise (sert à compléter une collection).
    const pushVariant = (product, variant) => {
      if (!product || !variant) return;
      if (variant.available === false) return;
      if (isExcluded(product)) return;
      const vid = String(variant.id);
      if (inCartVariants.has(vid) || pooledVariants.has(vid)) return;
      pooledVariants.add(vid);
      pooledProducts.add(String(product.id));
      pool.push({ product, variant });
    };
    // Ajoute un PRODUIT (variante = 1re disponible), hors produits déjà au panier.
    const pushProduct = (product) => {
      if (!product || !product.available) return;
      if (inCartProducts.has(String(product.id))) return;
      const variant = (product.variants ?? []).find((v) => v.available) ?? product.variants?.[0];
      pushVariant(product, variant);
    };

    // 1. COMPLÉTER LA COLLECTION (priorité) : autre(s) variante(s) d'un produit
    //    « à réunir » déjà au panier — Coffret 1 → Coffret 2, Boîte 1 → Boîte 2.
    const toComplete = cart.items.filter((item) => OBP_COMPLEMENTARY_PRODUCTS.has(item.product_id));
    const completeProducts = await Promise.all(toComplete.map((item) => this.#fetchProduct(item.handle)));
    completeProducts.forEach((product) => {
      (product?.variants ?? []).forEach((variant) => pushVariant(product, variant));
    });

    // 2. Compagnons remisés (règles bxgy déclenchées par le panier).
    const companions = companionsForCart(cart.items).filter((c) => c.handle);
    const companionProducts = await Promise.all(companions.map((c) => this.#fetchProduct(c.handle)));
    companionProducts.forEach(pushProduct);

    // 3. Produits complémentaires réglés À LA MAIN dans Search & Discovery
    //    (intent=complementary). Vide pour un produit sans réglage : jamais de
    //    choix automatique de Shopify (c'est lui qui proposait le tote bag).
    if (pool.length < MAX_UPSELLS) {
      const productIds = [...new Set(cart.items.map((item) => String(item.product_id)))];
      const lists = await Promise.all(productIds.map((pid) => this.#fetchComplementary(pid)));
      lists.flat().forEach(pushProduct);
    }

    // 4. Même univers puis autres (familles maison), en dernier recours.
    if (pool.length < MAX_UPSELLS) {
      const suggestions = suggestionsForCart(cart.items).filter(
        (s) => !OBP_UPSELL_EXCLUDED.has(String(s.productId)) && !redundant.has(String(s.productId))
      );
      // marge : certaines seront écartées (indisponibles, doublons entre elles)
      const needed = suggestions.slice(0, MAX_UPSELLS - pool.length + 4);
      const products = await Promise.all(needed.map((s) => this.#fetchProduct(s.handle)));
      products.forEach(pushProduct);
    }

    return pool.slice(0, MAX_UPSELLS);
  }

  async #fetchComplementary(productId) {
    const cache = ObpCartUpsell.#complementaryCache;
    if (!cache.has(productId)) {
      const url = `/recommendations/products.json?product_id=${productId}&intent=complementary&limit=10`;
      const promise = fetch(url)
        .then((r) => (r.ok ? r.json() : { products: [] }))
        .then((data) => data.products ?? [])
        .catch(() => []);
      cache.set(productId, promise);
    }
    return cache.get(productId);
  }

  async #fetchProduct(handle) {
    const cache = ObpCartUpsell.#productCache;
    if (!cache.has(handle)) {
      const promise = fetch(`/products/${handle}.js`)
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
      cache.set(handle, promise);
    }
    return cache.get(handle);
  }

  #writeCarousel(cart, candidates) {
    const wrap = document.createElement('div');
    wrap.className = 'obp-upsell';

    const head = document.createElement('div');
    head.className = 'obp-upsell__head';
    const title = document.createElement('p');
    title.className = 'obp-upsell__title';
    title.textContent = 'Un autre cadeau à faire ?';
    head.appendChild(title);

    const arrows = document.createElement('div');
    arrows.className = 'obp-upsell__arrows';
    const prev = this.#arrowButton('Produits précédents', 'M15 18l-6-6 6-6');
    const next = this.#arrowButton('Produits suivants', 'M9 6l6 6-6 6');
    arrows.append(prev, next);
    head.appendChild(arrows);
    wrap.appendChild(head);

    const track = document.createElement('div');
    track.className = 'obp-upsell__track';

    for (const { product, variant } of candidates) {
      if (!variant) continue;
      const unitCents = toCents(variant.price ?? product.price);
      const { discountCents, discountedUnitCents } = priceIfAdded(cart.items, product.id, unitCents);
      track.appendChild(this.#card(product, variant, unitCents, discountCents, discountedUnitCents));
    }

    wrap.appendChild(track);
    prev.addEventListener('click', () => track.scrollBy({ left: -track.clientWidth, behavior: 'smooth' }));
    next.addEventListener('click', () => track.scrollBy({ left: track.clientWidth, behavior: 'smooth' }));

    this.#write(wrap);
  }

  #card(product, variant, unitCents, discountCents, discountedUnitCents) {
    const card = document.createElement('article');
    card.className = 'obp-upsell__card';

    // Photo de LA variante proposée (Coffret 2 ≠ Coffret 1), sinon celle du produit.
    const imgSrc =
      variant.featured_image?.src ?? product.featured_image?.src ?? product.featured_image ?? product.images?.[0];
    if (imgSrc) {
      const img = document.createElement('img');
      img.className = 'obp-upsell__img';
      img.loading = 'lazy';
      img.width = 64;
      img.height = 64;
      img.alt = '';
      img.src = `${imgSrc}${String(imgSrc).includes('?') ? '&' : '?'}width=160`;
      card.appendChild(img);
    }

    const info = document.createElement('div');
    info.className = 'obp-upsell__info';

    const name = document.createElement('p');
    name.className = 'obp-upsell__name';
    let label = String(product.title).replace(/ — /g, ' - ');
    // Produit multi-variantes (coffret, dessous-de-verre) : on annonce clairement
    // LA variante proposée (« Coffret 2 · … ») pour que le complément soit lisible.
    const multiVariant = (product.variants ?? []).length > 1;
    if (multiVariant && variant.title && variant.title !== 'Default Title') {
      const shortVariant = variant.title.split(' — ')[0].split(' - ')[0].trim();
      label = `${shortVariant} · ${label}`;
    }
    name.textContent = label;
    info.appendChild(name);

    const prices = document.createElement('p');
    prices.className = 'obp-upsell__prices';
    const price = document.createElement('span');
    price.className = 'obp-upsell__price';
    price.textContent = formatMoney(discountedUnitCents);
    prices.appendChild(price);
    if (discountCents > 0) {
      const compare = document.createElement('s');
      compare.className = 'obp-upsell__compare';
      compare.textContent = formatMoney(unitCents);
      prices.appendChild(compare);
      const saving = document.createElement('span');
      saving.className = 'obp-upsell__saving';
      saving.textContent = `−${formatMoney(discountCents)}`;
      prices.appendChild(saving);
    }
    info.appendChild(prices);
    card.appendChild(info);

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'obp-upsell__add';
    button.textContent = 'Ajouter';
    // Nom du produit dans le libellé accessible : lecteurs d'écran, et OBP Mesure
    // l'enregistre (on sait enfin QUELLE suggestion est ajoutée).
    button.setAttribute('aria-label', `Ajouter ${label}`);
    button.addEventListener('click', () => this.#addToCart(button, variant.id));
    card.appendChild(button);

    return card;
  }

  #arrowButton(label, path) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'obp-upsell__arrow';
    button.setAttribute('aria-label', label);
    button.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${path}"/></svg>`;
    return button;
  }

  async #addToCart(button, variantId) {
    button.disabled = true;
    button.textContent = '…';
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });
      if (!response.ok) throw new Error(`add.js ${response.status}`);
      const cart = await fetch('/cart.js').then((r) => r.json());
      // Sans payload `sections` : cart-items-component re-render le tiroir
      // via le section renderer (même mécanique qu'obp-offers.js).
      document.dispatchEvent(
        new CartUpdateEvent(cart, 'obp-cart-upsell', {
          source: 'obp-cart-upsell',
          itemCount: cart.item_count,
        })
      );
    } catch (error) {
      console.error('[OBP upsell] ajout impossible', error);
      button.disabled = false;
      button.textContent = 'Ajouter';
    }
  }

  /** Écrit le contenu en neutralisant notre propre MutationObserver. */
  #write(content) {
    this.#selfMutation = true;
    if (content === '') {
      this.replaceChildren();
    } else {
      this.replaceChildren(content);
    }
    // setTimeout (PAS requestAnimationFrame : jamais déclenché dans un onglet
    // masqué/throttlé) : le MutationObserver, en microtâche, voit nos propres
    // mutations AVANT ce timeout et les ignore bien.
    setTimeout(() => {
      this.#selfMutation = false;
    }, 0);
  }
}

/** Les endpoints .js/.json renvoient des centimes ; garde-fou si chaîne. */
function toCents(value) {
  if (typeof value === 'string') return Math.round(parseFloat(value) * 100) || 0;
  return value || 0;
}

// Prix ronds sans « ,00 » (49 €), centimes seulement s'il y en a (17,50 €).
function formatMoney(cents) {
  const digits = cents % 100 === 0 ? 0 : 2;
  return `${(cents / 100).toLocaleString('fr-FR', { minimumFractionDigits: digits, maximumFractionDigits: digits })} €`;
}

if (!customElements.get('obp-cart-upsell')) {
  customElements.define('obp-cart-upsell', ObpCartUpsell);
}

// ============================================================================
// OBP : code promo arrivé par lien (?discount=CODE, ou cookie discount_code
// posé par /discount/CODE). On l'applique au PANIER (cart/update.js, même
// mécanique que cart-discount.js) pour que le tiroir affiche la pastille du
// code et les totaux remisés, au lieu d'attendre le checkout.
// Correctif du 23/10/2026 (codes du ticket d'or) : le cookie peut contenir un
// AUTRE code que celui posé ensuite (ticket gratté via Alia, code tapé dans le
// tiroir). Avant, on ajoutait ce vieux code au panier à chaque page : comme les
// codes du ticket ne se cumulent pas, Shopify désactivait le code gratté.
// Désormais : jamais si le panier a déjà un code, et une seule fois par visite.
// ============================================================================
let urlDiscountDone = false;
let urlCodeHandedToShopify = false;
const URL_DISCOUNT_KEY = 'obp_code_lien_applique';

function readSession(key) {
  try {
    return sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function writeSession(key, value) {
  try {
    sessionStorage.setItem(key, value);
  } catch (e) {}
}

async function applyUrlDiscount() {
  if (urlDiscountDone) return;
  // Lien au format ?discount=CODE (emails Klaviyo) : Shopify ne lit pas ce paramètre
  // et le code se perdait dès que le client changeait de page. On le confie une fois à
  // Shopify par son lien officiel /discount/CODE : Shopify le garde pendant la visite et
  // l'ajoute au panier, comme n'importe quel lien de réduction.
  const urlCode = new URLSearchParams(window.location.search).get('discount');
  if (urlCode && !urlCodeHandedToShopify) {
    urlCodeHandedToShopify = true;
    try {
      await fetch(`/discount/${encodeURIComponent(urlCode)}`);
      const data = await fetch('/cart.js').then((r) => r.json());
      if (data.item_count) {
        document.dispatchEvent(
          new CartUpdateEvent(data, 'obp-cart-upsell', { source: 'obp-url-discount', itemCount: data.item_count })
        );
      }
    } catch (error) {
      console.error('[OBP code promo URL]', error);
    }
  }
  let code = null;
  const match = document.cookie.match(/(?:^|;\s*)discount_code=([^;]+)/);
  if (match) code = decodeURIComponent(match[1]);
  // déjà appliqué une fois pendant cette visite : on respecte un retrait ou un remplacement
  if (!code || (readSession(URL_DISCOUNT_KEY) || '').toUpperCase() === code.toUpperCase()) {
    urlDiscountDone = true;
    return;
  }

  try {
    const cart = await fetch('/cart.js').then((r) => r.json());
    // un code ne s'applique qu'à un panier non vide : on retentera après le 1er ajout
    if (!cart.item_count) return;

    // le panier a déjà un code (ticket d'or, code tapé, lien précédent) : on n'y touche pas
    if ((cart.discount_codes ?? []).length) {
      urlDiscountDone = true;
      return;
    }

    const response = await fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: code }),
    });
    const data = await response.json();
    urlDiscountDone = true;
    writeSession(URL_DISCOUNT_KEY, code);

    const applied = (data.discount_codes ?? []).some(
      (d) => d.code.toUpperCase() === code.toUpperCase() && d.applicable
    );
    if (applied) {
      document.dispatchEvent(
        new CartUpdateEvent(data, 'obp-cart-upsell', {
          source: 'obp-url-discount',
          itemCount: data.item_count,
        })
      );
    }
  } catch (error) {
    console.error('[OBP code promo URL]', error);
  }
}

applyUrlDiscount();
document.addEventListener(CartAddEvent.eventName, () => {
  // léger délai : laisse l'ajout aboutir côté panier
  setTimeout(applyUrlDiscount, 300);
});
